import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  Eye, EyeOff, Mail, Lock, User, Check,
  Phone, ChevronRight, ChevronLeft, Building2,
  Store, Scissors, ShoppingCart, Beef, Pill,
  Coffee, UtensilsCrossed, Shirt, Smartphone,
  BookOpen, Leaf, Settings, Rocket, CalendarClock, Gem
} from 'lucide-react';

const COMMERCE_TYPES = [
  { key: 'boulangerie',    label: 'Boulangerie',      icon: Store           },
  { key: 'epicerie',       label: 'Epicerie',         icon: ShoppingCart    },
  { key: 'boucherie',      label: 'Boucherie',        icon: Beef            },
  { key: 'pharmacie',      label: 'Pharmacie',        icon: Pill            },
  { key: 'cafe',           label: 'Cafe / Snack',     icon: Coffee          },
  { key: 'restaurant',     label: 'Restaurant',       icon: UtensilsCrossed },
  { key: 'coiffeur',       label: 'Coiffeur',         icon: Scissors        },
  { key: 'vetements',      label: 'Vetements',        icon: Shirt           },
  { key: 'electronique',   label: 'Electronique',     icon: Smartphone      },
  { key: 'librairie',      label: 'Librairie',        icon: BookOpen        },
  { key: 'fruits_legumes', label: 'Fruits & Legumes', icon: Leaf            },
  { key: 'bijouterie',     label: 'Bijouterie',       icon: Gem             }, 
  { key: 'autre',          label: 'Autre',            icon: Store           },
];

const StepBar = ({ step, total }) => (
  <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1.5rem' }}>
    {Array.from({ length: total }).map((_, i) => (
      <div key={i} style={{
        height: 4, flex: 1, borderRadius: 99,
        background: i < step ? 'var(--primary)' : 'var(--bg)',
        boxShadow: i < step ? '0 1px 4px rgba(0,201,167,.4)' : 'var(--neu-in-sm)',
        transition: 'background 0.3s',
      }}/>
    ))}
  </div>
);

const CheckToggle = ({ checked, onChange, label, sub }) => (
  <div
    onClick={onChange}
    role="checkbox" aria-checked={checked} tabIndex={0}
    onKeyPress={e => e.key === 'Enter' && onChange()}
    style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
      padding: '0.9rem 1rem', borderRadius: 'var(--r-lg)', cursor: 'pointer',
      background: 'var(--bg)',
      boxShadow: checked ? 'var(--neu-in-sm)' : 'var(--neu-sm)',
      transition: 'box-shadow 0.2s',
    }}
  >
    <div style={{
      width: 22, height: 22, borderRadius: 7, flexShrink: 0, marginTop: 1,
      background: checked ? 'linear-gradient(145deg,#00d9b4,#009e85)' : 'var(--bg)',
      boxShadow: checked ? '2px 2px 6px rgba(0,158,133,.40)' : 'var(--neu-in-sm)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'all 0.18s',
    }}>
      {checked && <Check size={13} color="#fff" strokeWidth={3}/>}
    </div>
    <div>
      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text)' }}>{label}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: 'var(--text-2)', marginTop: '0.15rem', lineHeight: 1.4 }}>{sub}</div>}
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════════ */
const Register = () => {
  const [step, setStep]       = useState(1);
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const { register }          = useAuth();
  const navigate              = useNavigate();

  const [account, setAccount] = useState({ firstName:'', lastName:'', email:'', password:'', confirm:'' });
  const [shop, setShop]       = useState({ shopName:'', commerceType:'', phone:'', shopEmail:'', showPhone:true, showOwner:false });
  const [options, setOptions] = useState({ scheduleOnly: false });

  const setA = k => e => setAccount(p => ({ ...p, [k]: e.target.value }));
  const setS = k => e => setShop(p => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));
  const defaultShopName = () => account.firstName.trim() ? `Magasin ${account.firstName.trim()}` : 'Mon magasin';

  const validateStep1 = () => {
    if (!account.firstName.trim())     return 'Le prenom est obligatoire.';
    if (!account.email.trim())         return "L'email est obligatoire.";
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRx.test(account.email.trim())) return "Adresse email invalide.";
    if (account.password.length < 6)   return 'Mot de passe : 6 caracteres minimum.';
    if (account.password !== account.confirm) return 'Les mots de passe ne correspondent pas.';
    return null;
  };
  const validateStep2 = () => !shop.commerceType ? 'Choisissez un type de commerce.' : null;

  const goNext = () => {
    setError('');
    if (step === 1) { const e = validateStep1(); if (e) { setError(e); return; } }
    if (step === 2) { const e = validateStep2(); if (e) { setError(e); return; } }
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      const fullName = [account.firstName, account.lastName].filter(Boolean).join(' ');
      const finalName = shop.shopName.trim() || defaultShopName();
      const fbUser = await register(account.email.trim(), account.password, fullName, 'merchant');
      const ownerName = [account.firstName.trim(), account.lastName.trim()].filter(Boolean).join(' ');
      await updateDoc(doc(db, 'merchants', fbUser.uid), {
        firstName:         account.firstName.trim(),
        lastName:          account.lastName.trim(),
        ownerName,                          // ← champ composite pour ShopForm
        commerceType:      shop.commerceType,
        phone:             shop.phone.trim(),
        shopEmail:         shop.shopEmail.trim(),
        showPhone:         shop.showPhone,
        showOwner:         shop.showOwner,
        defaultShopName:   finalName,
        defaultManagedBy:  options.scheduleOnly ? 'schedule' : 'merchant',
      });
      navigate('/merchant');
    } catch (err) { setError(err.message || "Erreur lors de l'inscription."); setStep(1); }
    finally { setLoading(false); }
  };

  const pwdStr = account.password.length < 6 ? 0 : account.password.length < 10 ? 1 : 2;

  return (
    <div className="auth-page">
      <div className="auth-hero">
        <div style={{
          width: 68, height: 68, background: 'rgba(255,255,255,.15)', borderRadius: 20,
          border: '1px solid rgba(255,255,255,.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', marginBottom: '0.2rem',
        }}>
          <Store size={30} color="#fff" strokeWidth={2}/>
        </div>
        <h1>Devenir commercant</h1>
        <p>
          {step === 1 ? 'Creez votre compte securise'
            : step === 2 ? 'Presentez votre magasin'
            : 'Configurez le mode de gestion'}
        </p>
      </div>

      <div className="auth-body">
        <StepBar step={step} total={3}/>

        <div style={{ marginBottom: '1.35rem' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            padding: '0.22rem 0.75rem',
            background: 'var(--bg)', borderRadius: 'var(--r-full)',
            boxShadow: 'var(--neu-in-sm)', marginBottom: '0.5rem',
          }}>
            <span style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
              Etape {step} / 3
            </span>
          </div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 800, letterSpacing: '-0.3px' }}>
            {['Votre compte', 'Votre magasin', 'Mode de gestion'][step - 1]}
          </h2>
        </div>

        {error && <div className="alert alert-error" style={{ margin: '0 0 1.1rem' }}><span>{error}</span></div>}

        {/* ── Etape 1 ── */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label" htmlFor="r-fn"><User size={12} strokeWidth={2.5}/>Prenom *</label>
                <div className="form-input-wrap">
                  <User size={15} className="form-input-icon"/>
                  <input id="r-fn" className="form-input" type="text" value={account.firstName}
                    onChange={setA('firstName')} placeholder="Prénom" autoComplete="given-name" required/>
                </div>
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label" htmlFor="r-ln">Nom</label>
                <input id="r-ln" className="form-input" type="text" value={account.lastName}
                  onChange={setA('lastName')} placeholder="Nom" autoComplete="family-name"/>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="r-em"><Mail size={12} strokeWidth={2.5}/>Email *</label>
              <div className="form-input-wrap">
                <Mail size={15} className="form-input-icon"/>
                <input id="r-em" className="form-input" type="email" value={account.email}
                  onChange={e => setAccount(p => ({ ...p, email: e.target.value.trim() }))}
                  placeholder="vous@email.com" autoComplete="email" required/>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="r-pw"><Lock size={12} strokeWidth={2.5}/>Mot de passe *</label>
              <div style={{ position: 'relative' }}>
                <div className="form-input-wrap">
                  <Lock size={15} className="form-input-icon"/>
                  <input id="r-pw" className="form-input" type={showPwd ? 'text' : 'password'}
                    value={account.password} onChange={setA('password')}
                    placeholder="Min. 6 caracteres" style={{ paddingRight: '3rem' }}
                    autoComplete="new-password" required/>
                </div>
                <button type="button" onClick={() => setShowPwd(!showPwd)} aria-label={showPwd ? 'Masquer' : 'Afficher'}
                  style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', padding: '0.35rem', display: 'flex', alignItems: 'center' }}>
                  {showPwd ? <EyeOff size={17}/> : <Eye size={17}/>}
                </button>
              </div>
              {account.password && (
                <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', marginTop: '0.45rem' }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{
                      height: 3, flex: 1, borderRadius: 99, transition: 'all 0.25s',
                      background: pwdStr > i ? (pwdStr === 1 ? 'var(--warning)' : 'var(--primary)') : 'var(--bg)',
                      boxShadow: pwdStr > i ? 'none' : 'var(--neu-in-sm)',
                    }}/>
                  ))}
                  <span style={{ fontSize: '0.68rem', color: 'var(--text-2)', whiteSpace: 'nowrap', minWidth: 45 }}>
                    {pwdStr === 0 ? 'Trop court' : pwdStr === 1 ? 'Moyen' : 'Fort'}
                  </span>
                </div>
              )}
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="r-cf"><Lock size={12} strokeWidth={2.5}/>Confirmer *</label>
              <div className="form-input-wrap">
                <Lock size={15} className="form-input-icon"/>
                <input id="r-cf" className="form-input" type="password" value={account.confirm}
                  onChange={setA('confirm')} placeholder="••••••••" autoComplete="new-password" required/>
              </div>
              {account.confirm && account.password !== account.confirm && (
                <p style={{ fontSize: '0.72rem', color: 'var(--error)', marginTop: '0.3rem' }}>
                  Les mots de passe ne correspondent pas
                </p>
              )}
              {account.confirm && account.password === account.confirm && account.password.length >= 6 && (
                <p style={{ fontSize: '0.72rem', color: 'var(--primary)', marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Check size={12} strokeWidth={3}/> Mots de passe identiques
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── Etape 2 ── */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="r-sn"><Store size={12} strokeWidth={2.5}/>Nom du magasin</label>
              <div className="form-input-wrap">
                <Store size={15} className="form-input-icon"/>
                <input id="r-sn" className="form-input" type="text" value={shop.shopName}
                  onChange={setS('shopName')} placeholder={defaultShopName()}/>
              </div>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginTop: '0.3rem' }}>
                Laisser vide pour utiliser : "{defaultShopName()}"
              </p>
            </div>

            <div>
              <label className="form-label" style={{ display: 'flex', marginBottom: '0.65rem' }}>
                <Building2 size={12} strokeWidth={2.5}/>Type de commerce *
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '0.5rem' }}>
                {COMMERCE_TYPES.map(t => {
                  const Icon = t.icon;
                  const sel = shop.commerceType === t.key;
                  return (
                    <button key={t.key} type="button" onClick={() => setShop(p => ({ ...p, commerceType: t.key }))}
                      aria-pressed={sel}
                      style={{
                        padding: '0.7rem 0.35rem', borderRadius: 'var(--r-lg)', border: 'none',
                        cursor: 'pointer', fontFamily: 'inherit',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem',
                        background: 'var(--bg)',
                        boxShadow: sel ? 'var(--neu-in-sm)' : 'var(--neu-sm)',
                        transition: 'box-shadow 0.18s',
                      }}>
                      <Icon size={18} strokeWidth={1.75} color={sel ? 'var(--primary)' : 'var(--text-2)'}/>
                      <span style={{ fontSize: '0.62rem', fontWeight: 700, color: sel ? 'var(--primary)' : 'var(--text-2)', textAlign: 'center', lineHeight: 1.25 }}>
                        {t.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="r-ph"><Phone size={12} strokeWidth={2.5}/>Telephone</label>
              <div className="form-input-wrap">
                <Phone size={15} className="form-input-icon"/>
                <input id="r-ph" className="form-input" type="tel" value={shop.phone}
                  onChange={setS('phone')} placeholder="0555 123 456" autoComplete="tel"/>
              </div>
            </div>

            {shop.phone && (
              <CheckToggle checked={shop.showPhone} onChange={() => setShop(p => ({ ...p, showPhone: !p.showPhone }))}
                label="Afficher mon numero aux clients"
                sub="Les clients pourront vous appeler depuis l'application."/>
            )}

            <CheckToggle checked={shop.showOwner} onChange={() => setShop(p => ({ ...p, showOwner: !p.showOwner }))}
              label="Afficher mon nom sur la fiche"
              sub={`Les clients verront "${[account.firstName, account.lastName].filter(Boolean).join(' ') || 'votre nom'}" comme proprietaire.`}/>
          </div>
        )}

        {/* ── Etape 3 ── */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            {/* Recap */}
            <div style={{ padding: '1rem', borderRadius: 'var(--r-lg)', background: 'var(--bg)', boxShadow: 'var(--neu-in-sm)' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '0.6rem' }}>
                Recapitulatif
              </div>
              {[
                ['Proprietaire', [account.firstName, account.lastName].filter(Boolean).join(' ') || '—'],
                ['Magasin',      shop.shopName.trim() || defaultShopName()],
                ['Type',         COMMERCE_TYPES.find(t => t.key === shop.commerceType)?.label || '—'],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', padding: '0.18rem 0' }}>
                  <span style={{ color: 'var(--text-2)' }}>{label}</span>
                  <span style={{ fontWeight: 700, color: 'var(--text)' }}>{value}</span>
                </div>
              ))}
            </div>

            <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.7px' }}>
              Mode de gestion
            </div>

            {[
              { key: false, icon: Settings, title: 'Je gere moi-meme',     desc: 'Vous ouvrez/fermez manuellement. Statut fiable en temps reel.' },
              { key: true,  icon: CalendarClock, title: 'Gere par les horaires', desc: "Pas de smartphone ? Le statut se met a jour automatiquement selon vos horaires. Les clients voient un badge 'non confirmé en temps reel'." },
            ].map(opt => {
              const Icon = opt.icon;
              const sel = options.scheduleOnly === opt.key;
              return (
                <div key={String(opt.key)}
                  onClick={() => setOptions(p => ({ ...p, scheduleOnly: opt.key }))}
                  role="radio" aria-checked={sel} tabIndex={0}
                  onKeyPress={e => e.key === 'Enter' && setOptions(p => ({ ...p, scheduleOnly: opt.key }))}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.85rem',
                    padding: '1rem', borderRadius: 'var(--r-lg)', cursor: 'pointer',
                    background: 'var(--bg)',
                    boxShadow: sel ? 'var(--neu-in-sm)' : 'var(--neu-sm)',
                    transition: 'box-shadow 0.2s',
                  }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 'var(--r-md)', flexShrink: 0,
                    background: sel ? 'var(--primary-subtle)' : 'var(--bg)',
                    boxShadow: sel ? 'var(--neu-in-sm)' : 'var(--neu-sm)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={18} color={sel ? 'var(--primary)' : 'var(--text-2)'} strokeWidth={2}/>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.25rem', color: 'var(--text)' }}>
                      {opt.title}
                    </div>
                    <div style={{ fontSize: '0.76rem', color: 'var(--text-2)', lineHeight: 1.5 }}>{opt.desc}</div>
                  </div>
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                    background: sel ? 'linear-gradient(145deg,#00d9b4,#009e85)' : 'var(--bg)',
                    boxShadow: sel ? '2px 2px 6px rgba(0,158,133,.40)' : 'var(--neu-in-sm)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.18s',
                  }}>
                    {sel && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }}/>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', gap: '0.65rem', marginTop: '1.5rem' }}>
          {step > 1 && (
            <button type="button" onClick={() => { setError(''); setStep(s => s-1); }}
              className="btn btn-icon" style={{ width: 52, height: 52, flexShrink: 0 }} aria-label="Retour">
              <ChevronLeft size={20}/>
            </button>
          )}
          {step < 3 ? (
            <button type="button" onClick={goNext} className="btn btn-primary btn-large" style={{ flex: 1, margin: 0 }}>
              Suivant <ChevronRight size={18}/>
            </button>
          ) : (
            <button type="button" onClick={handleSubmit} className="btn btn-primary btn-large" disabled={loading} style={{ flex: 1, margin: 0 }}>
              {loading ? 'Creation…' : <><Rocket size={17}/>Creer mon compte</>}
            </button>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: '1.25rem', color: 'var(--text-2)', fontSize: '0.88rem' }}>
          Deja un compte ?{' '}
          <Link to="/login" style={{ color: 'var(--primary)', fontWeight: 700, textDecoration: 'none' }}>
            Se connecter
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;